import { create } from 'zustand';
import { getOpenItems } from '@/api/generated/open-items/open-items';
import { FacilityFlowCoreModulesOpenItemsEntitiesOpenItemKind } from '@/api/generated/models';
import type {
  FacilityFlowCoreModulesOpenItemsDtosCreateOpenItemRequest,
  FacilityFlowCoreModulesOpenItemsDtosOpenItemDto,
  FacilityFlowCoreModulesOpenItemsDtosOpenItemMessageDto,
  FacilityFlowCoreModulesOpenItemsDtosUpdateOpenItemRequest,
} from '@/api/generated/models';
import { showApiError } from '@/errors/showApiError';
import { i18n } from '@/i18n';
import { useToastStore } from '@/store/useToastStore';

export type OpenItemDto = FacilityFlowCoreModulesOpenItemsDtosOpenItemDto;
export type OpenItemMessageDto = FacilityFlowCoreModulesOpenItemsDtosOpenItemMessageDto;
export type OpenItemRequest = FacilityFlowCoreModulesOpenItemsDtosCreateOpenItemRequest &
  FacilityFlowCoreModulesOpenItemsDtosUpdateOpenItemRequest;
/** A new report ("Meldung"): text and an optional object — no assignee, no due date. */
export interface ReportRequest {
  title: string;
  note: string | null;
  objectId: string | null;
}
/**
 * `all` is administration only — the server answers 403 for everyone else. `reports`: the
 * caller's own reports, every report for administration.
 */
export type OpenItemScope = 'mine' | 'all' | 'reports';

const api = getOpenItems();

interface OpenItemsState {
  /** Open items of the current `scope` — the page's list, sorted by the server (due date). */
  items: OpenItemDto[];
  scope: OpenItemScope;
  includeCompleted: boolean;
  /** Open items of the caller (`scope=mine`) — feeds the dashboard cards. */
  myItems: OpenItemDto[];
  /** Sidebar badge: size of the latest `mine` fetch, independent of the page's scope. */
  myOpenCount: number;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchOpenItems: (scope?: OpenItemScope) => Promise<void>;
  /** Refetches `mine` and only updates `myItems`/`myOpenCount`, never the page list. */
  refreshMyCount: () => Promise<void>;
  createOpenItem: (data: OpenItemRequest) => Promise<OpenItemDto | null>;
  updateOpenItem: (id: string, data: OpenItemRequest) => Promise<OpenItemDto | null>;
  completeOpenItem: (id: string) => Promise<boolean>;
  reopenOpenItem: (id: string) => Promise<boolean>;
  deleteOpenItem: (id: string) => Promise<boolean>;
  setIncludeCompleted: (value:boolean) => Promise<void>;
  /** Sends a report to Verwaltung; any role may. */
  createReport: (data: ReportRequest) => Promise<OpenItemDto | null>;
  /**
   * Links a report to the task created from it and completes the report (administration
   * only). The task itself is created beforehand via the regular task store.
   */
  convertReport: (id: string, taskId: string) => Promise<OpenItemDto | null>;

  /** Message thread of the item whose message view is open; null when none is. */
  thread: { itemId: string; messages: OpenItemMessageDto[] } | null;
  threadLoading: boolean;
  threadError: string | null;
  sendingMessage: boolean;
  /** Loads the thread and marks it read for the caller's side (assignee or whole Verwaltung). */
  openThread: (itemId: string) => Promise<void>;
  sendMessage: (itemId: string, text: string) => Promise<boolean>;
  closeThread: () => void;
}

/** Applies `patch` to the item in both lists the store keeps. */
function patchItem(
  state: Pick<OpenItemsState, 'items' | 'myItems'>,
  itemId: string,
  patch: (item: OpenItemDto) => OpenItemDto,
): Pick<OpenItemsState, 'items' | 'myItems'> {
  const apply = (list: OpenItemDto[]) => list.map((item) => (item.id === itemId ? patch(item) : item));
  return { items: apply(state.items), myItems: apply(state.myItems) };
}

function toast(key: string) {
  useToastStore.getState().addToast(i18n.t(key), 'success');
}

export const useOpenItemsStore = create<OpenItemsState>((set, get) => {
  /** Reloads the page list; the badge needs its own call only when the page shows `all`. */
  const refreshAfterMutation = async () => {
    await get().fetchOpenItems();
    if (get().scope !== 'mine') await get().refreshMyCount();
  };

  return {
    items: [],
    scope: 'mine',
    includeCompleted: false,
    myItems: [],
    myOpenCount: 0,
    loading: false,
    saving: false,
    error: null,

    fetchOpenItems: async (scope) => {
      const nextScope = scope ?? get().scope;
      const includeCompleted = get().includeCompleted;          // (1) aus dem State lesen
      set({ loading: true, error: null, scope: nextScope });
      try {
        const data = await api.getApiV1OpenItems({ scope: nextScope, includeCompleted }); // (2) mitschicken
        if (get().scope !== nextScope || get().includeCompleted !== includeCompleted) return; // (3) Race-Guard
        set({ items: data, loading: false });
        if (nextScope === 'mine' && !includeCompleted) set({ myItems: data, myOpenCount: data.length }); // (4)
      } catch {
        if (get().scope !== nextScope || get().includeCompleted !== includeCompleted) return;
        set({ error: 'openItems:errors.fetchFailed', loading: false });
      }
    },

    refreshMyCount: async () => {
      try {
        const data = await api.getApiV1OpenItems({ scope: 'mine' });
        set({ myItems: data, myOpenCount: data.length });
      } catch {
        // Badge and dashboard card keep their last value; the page shows its own error state.
      }
    },

    createOpenItem: async (data) => {
      set({ saving: true });
      try {
        const created = await api.postApiV1OpenItems(data);
        set({ saving: false });
        toast('openItems:messages.created');
        await refreshAfterMutation();
        return created;
      } catch (err) {
        set({ saving: false });
        showApiError(err, { skipIfFieldErrors: false });
        return null;
      }
    },

    updateOpenItem: async (id, data) => {
      set({ saving: true });
      try {
        const updated = await api.putApiV1OpenItemsId(id, data);
        set({ saving: false });
        toast('openItems:messages.updated');
        await refreshAfterMutation();
        return updated;
      } catch (err) {
        set({ saving: false });
        showApiError(err, { skipIfFieldErrors: false });
        return null;
      }
    },

    completeOpenItem: async (id) => {
      try {
        await api.postApiV1OpenItemsIdComplete(id);
        if (get().includeCompleted) {
          await get().fetchOpenItems();
        } else {
          set((s) => ({ items: s.items.filter((item) => item.id !== id) }));
        }
        useToastStore.getState().addToast(i18n.t('openItems:messages.completed'), 'success', {
          duration: 8000,
          action: {
            label: i18n.t('openItems:actions.undo'),
            onClick: () => {
              void get().reopenOpenItem(id);
            },
          },
        });
        await get().refreshMyCount();
        return true;
      } catch (err) {
        showApiError(err, { skipIfFieldErrors: false });
        return false;
      }
    },

    reopenOpenItem: async (id) => {
      try {
        await api.postApiV1OpenItemsIdReopen(id);
        await refreshAfterMutation();
        return true;
      } catch (err) {
        showApiError(err, { skipIfFieldErrors: false });
        return false;
      }
    },

    deleteOpenItem: async (id) => {
      set({ saving: true });
      try {
        await api.deleteApiV1OpenItemsId(id);
        set((s) => ({ items: s.items.filter((item) => item.id !== id), saving: false }));
        toast('openItems:messages.deleted');
        await get().refreshMyCount();
        return true;
      } catch (err) {
        set({ saving: false });
        showApiError(err, { skipIfFieldErrors: false });
        return false;
      }
    },

    setIncludeCompleted: async (value) => {
      set({ includeCompleted: value });
      await get().fetchOpenItems();
    },

    createReport: async (data) => {
      set({ saving: true });
      try {
        const created = await api.postApiV1OpenItems({
          ...data,
          kind: FacilityFlowCoreModulesOpenItemsEntitiesOpenItemKind.NUMBER_2,
        });
        set({ saving: false });
        toast('openItems:report.created');
        // why: a report is not the reporter's to-do, so only the reports list changes.
        if (get().scope === 'reports') await get().fetchOpenItems();
        return created;
      } catch (err) {
        set({ saving: false });
        showApiError(err, { skipIfFieldErrors: false });
        return null;
      }
    },

    convertReport: async (id, taskId) => {
      try {
        const converted = await api.postApiV1OpenItemsIdConvert(id, { taskId });
        toast('openItems:report.converted');
        await refreshAfterMutation();
        return converted;
      } catch (err) {
        showApiError(err, { skipIfFieldErrors: false });
        return null;
      }
    },

    thread: null,
    threadLoading: false,
    threadError: null,
    sendingMessage: false,

    openThread: async (itemId) => {
      set({ thread: { itemId, messages: [] }, threadLoading: true, threadError: null });
      try {
        const messages = await api.getApiV1OpenItemsIdMessages(itemId);
        // A slow answer for an item the user already left must not overwrite the current one.
        if (get().thread?.itemId !== itemId) return;
        set({ thread: { itemId, messages }, threadLoading: false });
        // why: separate call so a plain list refresh never swallows a new message — only
        // opening this view counts as having read it.
        await api.postApiV1OpenItemsIdMessagesSeen(itemId);
        set((s) => patchItem(s, itemId, (item) => ({ ...item, unreadMessageCount: 0 })));
      } catch {
        if (get().thread?.itemId !== itemId) return;
        set({ threadLoading: false, threadError: 'openItems:thread.errors.fetchFailed' });
      }
    },

    sendMessage: async (itemId, text) => {
      set({ sendingMessage: true });
      try {
        const message = await api.postApiV1OpenItemsIdMessages(itemId, { text });
        set((s) => ({
          sendingMessage: false,
          thread:
            s.thread?.itemId === itemId
              ? { itemId, messages: [...s.thread.messages, message] }
              : s.thread,
          ...patchItem(s, itemId, (item) => ({ ...item, messageCount: (item.messageCount ?? 0) + 1 })),
        }));
        return true;
      } catch (err) {
        set({ sendingMessage: false });
        showApiError(err, { skipIfFieldErrors: false });
        return false;
      }
    },

    closeThread: () => set({ thread: null, threadLoading: false, threadError: null }),
  };
});

export default useOpenItemsStore;
